import React, { useCallback, useEffect, useState } from 'react';
import {
  Autocomplete,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import SearchableSelect from 'shared/components/SearchableSelect';
import { toast } from 'react-hot-toast';
import { fetchActiveTenantUsers } from 'shared/api/services/tenantUserService';
import { getRegistryTenantIdFromSession } from 'shared/utils/sessionProfileStore';
import { DECISION_TYPE, formatEnumLabel } from '../constants/enums';
import FormSection from './FormSection';

export const emptyBusinessProcessRow = () => ({
  key: `bp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  businessProcessId: '',
  decisionType: '',
  humanOversight: false,
  humanOversightExplanation: '',
  businessOwner: '',
});

const userFilter = createFilterOptions({
  stringify: (option) =>
    `${option.name ?? ''} ${option.email ?? ''} ${option.id ?? ''}`.toLowerCase(),
});

const userLabel = (u) => {
  if (!u) return '';
  const name = (u.name || '').trim();
  const email = (u.email || '').trim();
  if (name && email) return `${name} (${email})`;
  return name || email || '';
};

const findUserOption = (users, value) => {
  const raw = (value || '').trim();
  if (!raw) return null;
  const byEmail = users.find((u) => (u.email || '').trim().toLowerCase() === raw.toLowerCase());
  if (byEmail) return byEmail;
  const byName = users.find((u) => (u.name || '').trim().toLowerCase() === raw.toLowerCase());
  if (byName) return byName;
  const byLabel = users.find((u) => userLabel(u).toLowerCase() === raw.toLowerCase());
  if (byLabel) return byLabel;
  return { id: `legacy-${raw}`, name: raw, email: '' };
};

/**
 * Dialog to add / edit a business process use-case row.
 */
export default function BusinessProcessUseCaseDialog({
  open,
  initialValues,
  onClose,
  onSave,
  processOptions = [],
  usedProcessIds = new Set(),
}) {
  const [values, setValues] = useState(emptyBusinessProcessRow);
  const [errors, setErrors] = useState({});
  const [tenantUsers, setTenantUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const loadTenantUsers = useCallback(async () => {
    const registryTenantId = getRegistryTenantIdFromSession();
    if (!registryTenantId) {
      setTenantUsers([]);
      return;
    }
    setLoadingUsers(true);
    try {
      const list = await fetchActiveTenantUsers(registryTenantId, { page: 0, size: 500 });
      setTenantUsers(list);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to load tenant users';
      toast.error(typeof msg === 'string' ? msg : 'Failed to load users');
      setTenantUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setValues({ ...emptyBusinessProcessRow(), ...(initialValues || {}) });
    setErrors({});
    loadTenantUsers();
  }, [open, initialValues, loadTenantUsers]);

  const setField = (name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const availableProcesses = processOptions.filter(
    (opt) =>
      Number(opt.id) === Number(values.businessProcessId)
      || !usedProcessIds.has(Number(opt.id))
  );

  const validate = () => {
    const next = {};
    if (!values.businessProcessId) next.businessProcessId = 'Business process is required';
    if (!(values.decisionType || '').trim()) next.decisionType = 'Decision type is required';
    if (values.humanOversight && !(values.humanOversightExplanation || '').trim()) {
      next.humanOversightExplanation = 'Explain human oversight';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    onSave?.({
      ...values,
      businessOwner: (values.businessOwner || '').trim(),
      humanOversightExplanation: values.humanOversight
        ? (values.humanOversightExplanation || '').trim()
        : '',
    });
  };

  const ownerOption = findUserOption(tenantUsers, values.businessOwner);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {initialValues?.businessProcessId ? 'Edit business process' : 'Add business process'}
      </DialogTitle>
      <DialogContent dividers>
        <FormSection title="Use-case details">
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <SearchableSelect
                size="small"
                label="Business process"
                value={values.businessProcessId || ''}
                onChange={(next) => setField('businessProcessId', next)}
                error={Boolean(errors.businessProcessId)}
                helperText={errors.businessProcessId || ''}
                required
                placeholder="Select process…"
                options={availableProcesses.map((opt) => ({ value: opt.id, label: opt.name }))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                size="small"
                options={tenantUsers}
                loading={loadingUsers}
                filterOptions={userFilter}
                value={ownerOption}
                onChange={(_, option) =>
                  setField('businessOwner', option?.name || option?.email || '')
                }
                getOptionLabel={(option) => userLabel(option)}
                isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Business owner"
                    placeholder="Select owner…"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingUsers ? <CircularProgress color="inherit" size={14} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <SearchableSelect
                size="small"
                label="Decision type"
                value={values.decisionType || ''}
                onChange={(next) => setField('decisionType', next)}
                error={Boolean(errors.decisionType)}
                helperText={errors.decisionType || ''}
                required
                options={DECISION_TYPE.map((opt) => ({ value: opt, label: formatEnumLabel(opt) }))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel
                sx={{ mt: 0.5 }}
                control={
                  <Checkbox
                    size="small"
                    checked={Boolean(values.humanOversight)}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setValues((prev) => ({
                        ...prev,
                        humanOversight: checked,
                        humanOversightExplanation: checked ? prev.humanOversightExplanation : '',
                      }));
                      setErrors((prev) => {
                        if (!prev.humanOversightExplanation) return prev;
                        const next = { ...prev };
                        delete next.humanOversightExplanation;
                        return next;
                      });
                    }}
                  />
                }
                label="Human oversight"
              />
            </Grid>
            {values.humanOversight ? (
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  minRows={3}
                  label="Oversight explanation"
                  value={values.humanOversightExplanation || ''}
                  onChange={(e) => setField('humanOversightExplanation', e.target.value)}
                  error={Boolean(errors.humanOversightExplanation)}
                  helperText={errors.humanOversightExplanation || ''}
                />
              </Grid>
            ) : null}
          </Grid>
        </FormSection>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
